import { useEffect, useState } from 'react'
import { withTranslation } from 'react-i18next'

import Button from '@material-ui/core/Button'
import Chip from '@material-ui/core/Chip'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import TextField from '@material-ui/core/TextField'
import Alert from '@material-ui/lab/Alert'

import api from 'core/api'
import Loader from 'components/Loader'
import { useAppContext } from '../../../../App'

import {
  getEmailHost,
  getRemovedDomainsInUse,
  mapStoreSenderApiErrors,
  normalizeDomain,
  validateDefaultSender,
  validateDomain
} from '../../functions/store-sender.function'

import { Subtitle } from '../../style'
import { Section, Fields, Domains } from './style'

export const GET_ENDPOINT = 'StoreSettings/GetStoreSenderEmailConfiguration'
export const SAVE_ENDPOINT = 'StoreSettings/SaveStoreSenderEmailConfiguration'

const SenderEmailSettings = ({ t }) => {
  const { notify } = useAppContext()

  const [saved, setSaved] = useState(null)
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [domains, setDomains] = useState([])
  const [newDomain, setNewDomain] = useState('')
  const [newDomainError, setNewDomainError] = useState(null)
  const [errors, setErrors] = useState({})
  const [removedInUse, setRemovedInUse] = useState(null)
  const [saving, setSaving] = useState(false)

  const applyConfiguration = (configuration) => {
    setSaved(configuration)
    setEmail(configuration.DefaultSenderEmail ?? '')
    setName(configuration.DefaultSenderName ?? '')
    setDomains(configuration.AllowedDomains ?? [])
  }

  useEffect(() => {
    let isMounted = true

    api
      .get(GET_ENDPOINT)
      .then(({ data }) => {
        if (isMounted) applyConfiguration(data)
      })
      .catch(() => notify(t('storemanagement.sender.errors.load'), 'error'))

    return () => {
      isMounted = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const defaultHost = getEmailHost(email)

  const handleAddDomain = () => {
    const error = validateDomain(newDomain, domains)
    setNewDomainError(error)

    if (error) return

    setDomains([...domains, normalizeDomain(newDomain)].sort())
    setNewDomain('')
  }

  const handleRemoveDomain = (domain) => {
    setDomains(domains.filter((item) => item !== domain))
  }

  const save = () => {
    setSaving(true)
    setRemovedInUse(null)

    api
      .post(SAVE_ENDPOINT, {
        DefaultSenderEmail: email.trim(),
        DefaultSenderName: name.trim(),
        AllowedDomains: domains
      })
      .then(({ data }) => {
        if (data.HasErrors) {
          setErrors(mapStoreSenderApiErrors(data.ErrorList))
          return
        }

        setErrors({})
        applyConfiguration({
          ...data.Element,
          DomainUsages: data.Element.DomainUsages ?? saved?.DomainUsages
        })
        notify(t('storemanagement.sender.saved'), 'success')
      })
      .catch(() => notify(t('storemanagement.sender.errors.generic'), 'error'))
      .finally(() => setSaving(false))
  }

  const handleSave = () => {
    const fieldErrors = validateDefaultSender(email, name)
    setErrors(fieldErrors)

    if (Object.keys(fieldErrors).length > 0) return

    const inUse = getRemovedDomainsInUse(
      saved?.AllowedDomains,
      domains,
      email,
      saved?.DomainUsages
    )

    if (inUse.length > 0) {
      setRemovedInUse(inUse)
      return
    }

    save()
  }

  if (!saved) return <Loader noMargin size={40} />

  const domainErrors = Object.keys(errors).filter((field) =>
    field.startsWith('AllowedDomains')
  )

  return (
    <Section data-testid="sender-email-settings">
      <Subtitle>{t('storemanagement.sender.title')}</Subtitle>

      <Alert severity="info">{t('storemanagement.sender.aknaWarning')}</Alert>

      <Fields>
        <TextField
          id="defaultSenderEmail"
          label={t('storemanagement.sender.defaultEmail')}
          variant="outlined"
          size="small"
          fullWidth
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={!!errors.DefaultSenderEmail}
          helperText={
            errors.DefaultSenderEmail
              ? t(errors.DefaultSenderEmail)
              : t('storemanagement.sender.defaultEmailHint')
          }
        />
        <TextField
          id="defaultSenderName"
          label={t('storemanagement.sender.defaultName')}
          variant="outlined"
          size="small"
          fullWidth
          value={name}
          onChange={(e) => setName(e.target.value)}
          error={!!errors.DefaultSenderName}
          helperText={errors.DefaultSenderName && t(errors.DefaultSenderName)}
        />
      </Fields>

      <Subtitle>{t('storemanagement.sender.domains')}</Subtitle>

      <Domains data-testid="sender-domains">
        {defaultHost && defaultHost !== 'yourviews.com.br' && (
          <Chip
            label={`${defaultHost} (${t('storemanagement.sender.defaultDomain')})`}
            color="primary"
            variant="outlined"
          />
        )}
        {domains
          .filter((domain) => domain !== defaultHost)
          .map((domain) => (
            <Chip
              key={domain}
              label={domain}
              onDelete={() => handleRemoveDomain(domain)}
            />
          ))}
      </Domains>

      {domainErrors.map((field) => (
        <Alert key={field} severity="error">
          {`${domains[Number(field.replace(/\D/g, ''))] ?? ''} ${t(
            errors[field]
          )}`}
        </Alert>
      ))}

      <Fields>
        <TextField
          id="newSenderDomain"
          label={t('storemanagement.sender.newDomain')}
          placeholder="marca.com.br"
          variant="outlined"
          size="small"
          fullWidth
          value={newDomain}
          onChange={(e) => {
            setNewDomain(e.target.value)
            setNewDomainError(null)
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleAddDomain()
          }}
          error={!!newDomainError}
          helperText={newDomainError && t(newDomainError)}
        />
        <Button variant="outlined" color="primary" onClick={handleAddDomain}>
          {t('storemanagement.sender.addDomain')}
        </Button>
      </Fields>

      <Button
        variant="contained"
        color="primary"
        disabled={saving}
        onClick={handleSave}
        data-testid="btnSaveSender"
      >
        {t('storemanagement.sender.save')}
      </Button>

      <Dialog open={!!removedInUse} onClose={() => setRemovedInUse(null)}>
        <DialogTitle>{t('storemanagement.sender.confirmTitle')}</DialogTitle>
        <DialogContent>
          <p>{t('storemanagement.sender.confirmText')}</p>
          {(removedInUse ?? []).map(({ domain, campaigns }) => (
            <div key={domain}>
              <strong>{domain}</strong>
              <ul>
                {campaigns.map((campaign) => (
                  <li key={campaign.IdCampaign}>{campaign.Name}</li>
                ))}
              </ul>
            </div>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemovedInUse(null)}>
            {t('storemanagement.sender.cancel')}
          </Button>
          <Button color="primary" onClick={save} data-testid="btnConfirmSender">
            {t('storemanagement.sender.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Section>
  )
}

export default withTranslation()(SenderEmailSettings)
